import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import Card from '@mui/material/Card';
import { Stack, Button } from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { useGetCompanyDocumentsQuery, useRemoveCompanyDocumentMutation } from 'src/services/connections/connections.service';

import { useTable } from 'src/components/table';
import FileManagerTable from 'src/components/documents/file-manager-table';

import CompanyDocumentUploadModal from '../../modal/company-document-uploader';

const DEFAULT_PAGE = {
  page: 1,
  per_page: 10,
};

type Props = {
  profileId?: string;
  uploadVisible?: boolean;
};

export default function CompanyDocumentsView({ profileId, uploadVisible = true }: Props) {
  const [page, setPage] = useState(DEFAULT_PAGE);

  const showUploadModal = useBoolean();

  const { t } = useTranslate();

  const [removeDocument, { isLoading: isRemoving }] = useRemoveCompanyDocumentMutation();

  const { currentData: companyDocuments, isLoading } = useGetCompanyDocumentsQuery(
    {
      profile_id: profileId as string,
      params: page,
    },
    { skip: !profileId }
  );

  const table = useTable();

  const notFound = !companyDocuments?.count && !isLoading;

  const onAddSuccess = () => {
    showUploadModal.onFalse();
  };

  const handleDeleteItem = async (id: string) => {
    try {
      await removeDocument({
        company_doc_id: id,
        profile_id: profileId as string
      }).unwrap();

      enqueueSnackbar(t('companies.profile.api.delete_document.success'), { variant: 'success' });

    } catch (error) {
      enqueueSnackbar(t('companies.profile.api.delete_document.default_error'), { variant: 'error' });
    }
  };

  return (
    <>
      {
        uploadVisible && (
          <Stack direction="row" justifyContent="flex-end" my={2}>
            <Button variant="contained" disabled={!profileId} onClick={showUploadModal.onTrue}>
              {t('profile.documents.upload')}
            </Button>
          </Stack>
        )
      }
      <Card>
        <FileManagerTable
          table={table}
          dataFiltered={companyDocuments?.results ?? []}
          count={companyDocuments?.count ?? 0}
          onDeleteRow={handleDeleteItem}
          notFound={notFound}
          documentType="company_profile"
          onChangePage={(_e, pageNumber) => setPage({ ...page, page: pageNumber + 1 })}
          onChangePerPage={(e) => setPage({ ...page, per_page: Number(e.target.value) })}
          pageOptions={page}
        />
      </Card>

      {
        profileId && (
          <CompanyDocumentUploadModal
            open={showUploadModal.value}
            onClose={showUploadModal.onFalse}
            profileId={profileId}
            onUpload={onAddSuccess}
          />
        )
      }
    </>
  );
}